import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../api/auth.api';
import {
  resolveAppRoleFromRoles,
  type WhoAmIResponse,
} from '../../application/dto/Auth.dto';
import { RootState } from '../../../../store/store';
import { Keys } from '../../../../common/constants';

export type UserRole = 'mentor' | 'student' | 'none';
export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  identity: WhoAmIResponse | null;
  /** Thin projection for role routing / legacy UI — derived from identity. */
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName?: string;
    avatar: string;
    phone?: string;
    role: UserRole;
  } | null;
  /** Optional hybrid Bearer; cookie session is canonical. */
  accessToken: string | null;
  refreshToken: string | null;
  companyId: string | null;
  isLoading: boolean;
  error: string | null;
}

const emptyAuthState = (): AuthState => ({
  status: 'unknown',
  identity: null,
  user: null,
  accessToken: null,
  refreshToken: null,
  companyId: null,
  isLoading: false,
  error: null,
});

const persistAuthSession = (state: AuthState) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    Keys.COMMON,
    JSON.stringify({
      status: state.status,
      identity: state.identity,
      user: state.user,
      companyId: state.companyId,
    }),
  );
  localStorage.setItem(Keys.ACCESS_TOKEN, state.accessToken || '');
  localStorage.setItem(Keys.REFRESH_TOKEN, state.refreshToken || '');
};

const applyIdentity = (state: AuthState, identity: WhoAmIResponse) => {
  const role = resolveAppRoleFromRoles(identity.roles);
  state.status = 'authenticated';
  state.identity = identity;
  state.companyId = String(identity.companyId);
  state.user = {
    id: String(identity.id),
    email: identity.email || '',
    firstName: '',
    lastName: '',
    fullName: identity.email || '',
    avatar: '',
    phone: '',
    role,
  };
  state.isLoading = false;
  state.error = null;
  persistAuthSession(state);
};

const loadAuthState = (): AuthState => {
  if (typeof window === 'undefined') return emptyAuthState();
  try {
    // Cookie session is SoT — always start unknown until whoami settles.
    // Optional Bearer kept for hybrid prepareHeaders only.
    return {
      ...emptyAuthState(),
      accessToken: localStorage.getItem(Keys.ACCESS_TOKEN) || null,
      refreshToken: localStorage.getItem(Keys.REFRESH_TOKEN) || null,
    };
  } catch {
    return emptyAuthState();
  }
};

const initialState: AuthState = loadAuthState();

const AUTH_PENDING = [
  authApi.endpoints.signIn.matchPending,
  authApi.endpoints.signUp.matchPending,
  authApi.endpoints.signInWithGoogle.matchPending,
  authApi.endpoints.signUpWithGoogle.matchPending,
  authApi.endpoints.whoAmI.matchPending,
] as const;

const AUTH_REJECTED = [
  authApi.endpoints.signIn.matchRejected,
  authApi.endpoints.signUp.matchRejected,
  authApi.endpoints.signInWithGoogle.matchRejected,
  authApi.endpoints.signUpWithGoogle.matchRejected,
] as const;

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIdentity: (state, action: PayloadAction<WhoAmIResponse>) => {
      applyIdentity(state, action.payload);
    },
    setUnauthenticated: state => {
      state.status = 'unauthenticated';
      state.identity = null;
      state.user = null;
      state.companyId = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.error = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem(Keys.COMMON);
        localStorage.removeItem(Keys.ACCESS_TOKEN);
        localStorage.removeItem(Keys.REFRESH_TOKEN);
      }
    },
    setUser: (state, action: PayloadAction<AuthState['user']>) => {
      state.user = action.payload;
      persistAuthSession(state);
    },
    setTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      persistAuthSession(state);
    },
    setUserRole: (state, action: PayloadAction<UserRole>) => {
      if (state.user) state.user.role = action.payload;
      persistAuthSession(state);
    },
    logout: state => {
      state.status = 'unauthenticated';
      state.identity = null;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.companyId = null;
      state.error = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem(Keys.COMMON);
        localStorage.removeItem(Keys.ACCESS_TOKEN);
        localStorage.removeItem(Keys.REFRESH_TOKEN);
      }
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    AUTH_PENDING.forEach(matcher => {
      builder.addMatcher(matcher, state => {
        state.isLoading = true;
        state.error = null;
      });
    });
    AUTH_REJECTED.forEach(matcher => {
      builder.addMatcher(matcher, (state, { error }) => {
        state.isLoading = false;
        state.error = error.message || 'Authentication failed';
      });
    });
    builder.addMatcher(authApi.endpoints.whoAmI.matchFulfilled, (state, { payload }) => {
      applyIdentity(state, payload);
    });
    builder.addMatcher(authApi.endpoints.whoAmI.matchRejected, state => {
      state.isLoading = false;
      state.status = 'unauthenticated';
      state.identity = null;
      state.user = null;
      state.companyId = null;
    });
  },
});

export const selectAuthUser = (state: RootState) => state.auth.user;
export const selectAuthIdentity = (state: RootState) => state.auth.identity;
export const selectAuthStatus = (state: RootState) => state.auth.status;
export const selectAuthToken = (state: RootState) => state.auth.accessToken;
export const selectAuthRefreshToken = (state: RootState) =>
  state.auth.refreshToken;
export const selectAuthCompanyId = (state: RootState) => state.auth.companyId;
/** @deprecated use selectAuthCompanyId */
export const selectAuthTenantID = selectAuthCompanyId;
export const selectAuthIsAdmin = (_state: RootState) => false;
export const selectAuthIsLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectUserRole = (state: RootState) =>
  state.auth.user?.role || 'none';

export const {
  setIdentity,
  setUnauthenticated,
  setUser,
  setTokens,
  setUserRole,
  logout,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;
